import React from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Button from 'Components/DesignSystem/Button/Button';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';

type ConfirmDialogProps = {
    id: string;
    title: string;
    message: React.ReactNode;
    confirmText: string;
    cancelText?: string;
    ternaryText?: string;
    onClose: () => void;
    onConfirm: () => void;
    ternaryMethod?: () => void;
    saveInProgress?: boolean;
}

/**
 * The shared warn-then-confirm modal (ISR MainPageHeader ×2, DefineModel
 * CategoryImport, ARS MerchantSettingsModal, Computed Substitutions' iteration
 * editor) — re-skinned IN PLACE on the DS Dialog + warning Alert + Buttons
 * (2026-09-08), the `UnsavedChangesModal` recipe. Props, ids and testids are
 * unchanged, so all four consumers and their mocks keep working:
 *
 * - Cancel renders only when `cancelText` is given; the ternary only with BOTH
 *   `ternaryText` and `ternaryMethod` — as before.
 * - The ternary is the destructive exit (`danger`, was a `#bf2600` text button)
 *   and sits alone on the LEFT, apart from the safe Cancel/Confirm pair.
 * - `saveInProgress` disables the confirm and swaps its label to "Saving"
 *   (DS `loading` replaces the hand-rolled `LoadingIcon`).
 */
const ConfirmModal = ({ id, title, message, confirmText, cancelText, ternaryText, onClose, onConfirm, ternaryMethod, saveInProgress }: ConfirmDialogProps) => {

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title={title}
            size="md"
            data-testid={id}
            showCloseButton={false}
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    id={`${id}-close-btn`}
                    data-testid="rulecomponents-commitconfirmmodal-button-1"
                    onClick={onClose}
                >
                    <Cross size={16} />
                </IconButton>
            }
            footer={
                <div id={`${id}-actions`} className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    {/* The destructive exit sits apart from the safe pair — the
                        UnsavedChangesModal footer split. An empty div keeps the safe
                        pair right-aligned when there is no ternary. */}
                    <div>
                        {ternaryText && ternaryMethod && (
                            <Button
                                variant="danger"
                                className="tw-whitespace-nowrap"
                                id={`${id}-ternary-btn`}
                                data-testid="rulecomponents-commitconfirmmodal-button-2"
                                onClick={ternaryMethod}
                            >
                                {ternaryText}
                            </Button>
                        )}
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        {cancelText && (
                            <Button
                                variant="secondary"
                                className="tw-whitespace-nowrap"
                                id={`${id}-cancel-btn`}
                                data-testid="rulecomponents-commitconfirmmodal-button-3"
                                onClick={onClose}
                            >
                                {cancelText}
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            className="tw-whitespace-nowrap"
                            id={`${id}-confirm-btn`}
                            data-testid="rulecomponents-commitconfirmmodal-button-4"
                            onClick={onConfirm}
                            disabled={saveInProgress}
                            loading={saveInProgress}
                        >
                            {saveInProgress ? "Saving" : confirmText}
                        </Button>
                    </div>
                </div>
            }
        >
            <Alert tone="warning" data-testid={`${id}-message`}>
                {message}
            </Alert>
        </Dialog>
    );
}

export default ConfirmModal;
